import React, { useState } from 'react';
import { colors, API, authHeaders } from '../utils/theme';
import { Modal, Button } from './UI';
import { EMPRESAS, CamposCategoria } from './CamposClassificacao';

// ============================================================================
//  Classificar lançamentos (04/11/2026) — uma compra ou várias de uma vez, na
//  tela de Lançamentos, com as MESMAS rotas da Conciliação de fatura:
//
//    · categoria — compra com lançamento do TechMiles/manual: muda a do
//      lançamento (plano de contas); linha da fatura SEM par: vira avulso
//      conciliado com essa categoria (POST /conciliacao/avulso-lote), que é o
//      "registrar avulso" da Conciliação;
//    · empresa — na linha da fatura e na conciliação (PATCH /transacoes/empresa/bulk);
//    · operação — o centro de fechamento da linha da fatura, como escolha
//      manual; "pela regra" devolve à regra automática (PATCH /transacoes/centros/bulk);
//    · gasto pessoal — Pessoal, ou Pessoal X/J (retirada do sócio): conciliação
//      GASTO_PESSOAL + o centro (POST /conciliacao/gasto-pessoal).
// ============================================================================

const SEM_PAR = ['pendente', 'sugerido'];
const NAO_MEXER = '';
const LIMPAR = '__limpar__';
const REGRA = '__regra__';

async function chamar(caminho, metodo, corpo) {
  const r = await fetch(`${API}${caminho}`, {
    method: metodo, headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify(corpo),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || `Erro ${r.status}`);
  return d;
}

export default function ClassificarLancamentosModal({ grupos, operacoes, modoInicial = 'classificar', onClose, onFeito }) {
  const um = grupos.length === 1 ? grupos[0] : null;
  const [modo, setModo] = useState(modoInicial);
  const [cat, setCat] = useState({
    categoria: (um && um.categoria) || '', subcategoria: (um && um.subcategoria) || '',
    xm_categoria_id: (um && um.xm_categoria_id) || null,
  });
  // Linha pendente com categoria = avulso criado: a categoria só vai quando
  // alguém a escolhe (ou marca), nunca só porque veio preenchida.
  const [mudarCategoria, setMudarCategoria] = useState(!(um && um.categoria));
  // Uma compra: o select mostra o valor atual (LIMPAR = sem empresa / REGRA =
  // operação da regra). Várias: começa em "não mexer".
  const empresaInicial = um ? (um.empresa || LIMPAR) : NAO_MEXER;
  const operacaoInicial = um ? (um.centro || REGRA) : NAO_MEXER;
  const [empresa, setEmpresa] = useState(empresaInicial);
  const [operacao, setOperacao] = useState(operacaoInicial);
  const [pessoal, setPessoal] = useState('PESSOAL');
  const [busy, setBusy] = useState(false);
  const [resultado, setResultado] = useState(null);

  const semPar = grupos.filter(g => g.linha_banco_id && !g.id_operacao && SEM_PAR.includes(g.situacao));
  const comLancamento = grupos.filter(g => g.id_operacao);
  const soFatura = grupos.filter(g => g.linha_banco_id && !g.id_operacao && !SEM_PAR.includes(g.situacao));
  const daFatura = grupos.filter(g => g.linha_banco_id);
  const pessoaisPossiveis = grupos.filter(g => g.linha_banco_id && SEM_PAR.includes(g.situacao));
  const temCategoria = !!(cat.categoria && (cat.subcategoria || !cat.xm_categoria_id));

  const aplicar = async () => {
    setBusy(true);
    const feitos = [];
    const erros = [];
    const passo = async (rotulo, fn) => {
      try { const r = await fn(); if (r) feitos.push(r); } catch (e) { erros.push(`${rotulo}: ${e.message}`); }
    };
    if (modo === 'pessoal') {
      let marcados = 0;
      for (const g of pessoaisPossiveis) {
        await passo(`"${g.descricao}"`, async () => {
          await chamar('/conciliacao/gasto-pessoal', 'POST', { transacao_id: g.linha_banco_id });
          marcados++;
        });
      }
      if (marcados) feitos.push(`${marcados} marcado(s) como gasto pessoal`);
      if (pessoal !== 'PESSOAL' && pessoaisPossiveis.length) {
        await passo('Operação', async () => {
          const d = await chamar('/transacoes/centros/bulk', 'PATCH', { ids: pessoaisPossiveis.map(g => g.linha_banco_id), centro: pessoal });
          return `${d.atualizados} com ${operacoes.rotulo(pessoal)}`;
        });
      }
    } else {
      const empresaFinal = empresa === LIMPAR ? null : (empresa || null);
      if (mudarCategoria && temCategoria) {
        if (semPar.length) {
          await passo('Avulso', async () => {
            const d = await chamar('/conciliacao/avulso-lote', 'POST', {
              transacao_ids: semPar.map(g => g.linha_banco_id), categoria: cat.categoria, subcategoria: cat.subcategoria,
              xm_categoria_id: cat.xm_categoria_id || null, empresa: empresaFinal, conciliar: true,
            });
            (d.erros || []).forEach(e => erros.push(e));
            return d.criadas ? `${d.criadas} avulso(s) registrado(s) e conciliado(s)` : null;
          });
        }
        const reclassificar = [...comLancamento.map(g => g.id_operacao), ...soFatura.map(g => g.linha_banco_id)];
        if (reclassificar.length) {
          if (!cat.xm_categoria_id) {
            erros.push(`${reclassificar.length} lançamento(s) já com par: categoria criada no Finance só vale pro avulso — escolha uma subcategoria do plano de contas.`);
          } else {
            await passo('Categoria', async () => {
              const d = await chamar('/plano-contas/lancamentos/classificar', 'POST', { ids: reclassificar, xm_categoria_id: cat.xm_categoria_id });
              return `${d.atualizadas} lançamento(s) com a categoria nova`;
            });
          }
        }
      }
      if (empresa !== NAO_MEXER && empresa !== empresaInicial) {
        const ids = grupos.map(g => g.linha_banco_id || g.id_operacao).filter(Boolean);
        if (ids.length) {
          await passo('Empresa', async () => {
            const d = await chamar('/transacoes/empresa/bulk', 'PATCH', { ids, empresa: empresaFinal });
            return `${d.atualizados} com empresa ${empresaFinal || '(nenhuma)'}`;
          });
        }
      }
      if (operacao !== NAO_MEXER && operacao !== operacaoInicial) {
        if (daFatura.length) {
          const centro = operacao === REGRA ? null : operacao;
          await passo('Operação', async () => {
            const d = await chamar('/transacoes/centros/bulk', 'PATCH', { ids: daFatura.map(g => g.linha_banco_id), centro });
            return `${d.atualizados} com ${centro ? operacoes.rotulo(centro) : 'a operação da regra'}`;
          });
        } else {
          erros.push('Operação: só a linha da fatura tem operação — nenhuma das escolhidas tem fatura.');
        }
      }
    }
    setBusy(false);
    setResultado({ feitos, erros });
    if (feitos.length) onFeito && onFeito();
  };

  const lbl = { display: 'block', fontSize: 12, color: colors.textMuted, marginBottom: 4 };
  const inp = { padding: '8px 10px', borderRadius: 8, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: 13 };
  const sel = { ...inp, width: '100%' };
  const aba = (v, rotulo) => (
    <button type="button" onClick={() => { setModo(v); setResultado(null); }} style={{
      padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600,
      border: `1px solid ${modo === v ? colors.primary : colors.border}`,
      background: modo === v ? `${colors.primary}18` : 'transparent', color: modo === v ? colors.primary : colors.textMuted,
    }}>{rotulo}</button>
  );
  const nota = { fontSize: 12, color: colors.textMuted, lineHeight: 1.5 };

  return (
    <Modal isOpen onClose={onClose} width={640}
      title={um ? `Classificar: ${um.descricao}` : `Classificar ${grupos.length} lançamentos`}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
        {aba('classificar', 'Categoria · empresa · operação')}
        {aba('pessoal', 'Gasto pessoal')}
      </div>

      {modo === 'classificar' ? (
        <>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: colors.text, marginBottom: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={mudarCategoria} onChange={e => setMudarCategoria(e.target.checked)} />
            Categoria
          </label>
          {mudarCategoria && (
            <div style={{ marginBottom: 10 }}>
              <CamposCategoria categoria={cat.categoria} subcategoria={cat.subcategoria}
                empresa={empresa === LIMPAR || empresa === NAO_MEXER ? '' : empresa}
                onChange={(m) => setCat(c => ({ ...c, ...m }))} inp={inp} sel={sel} lbl={lbl} />
              <div style={{ ...nota, marginTop: 6 }}>
                {semPar.length > 0 && <div>• {semPar.length} sem lançamento no TechMiles: vira(m) <b>avulso</b> conciliado com esta categoria.</div>}
                {comLancamento.length > 0 && <div>• {comLancamento.length} com lançamento: a categoria do lançamento muda.</div>}
                {soFatura.length > 0 && <div>• {soFatura.length} já fechada(s) sem lançamento (pessoal/pagamento): a categoria da linha da fatura muda.</div>}
              </div>
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12, marginTop: 6 }}>
            <div>
              <label style={lbl}>Empresa</label>
              <select style={sel} value={empresa} onChange={e => setEmpresa(e.target.value)}>
                {!um && <option value={NAO_MEXER}>Não mexer</option>}
                <option value={LIMPAR}>{um ? '— sem empresa —' : 'Tirar a empresa'}</option>
                {EMPRESAS.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Operação</label>
              <select style={sel} value={operacao} onChange={e => setOperacao(e.target.value)} disabled={!daFatura.length}>
                {!um && <option value={NAO_MEXER}>Não mexer</option>}
                <option value={REGRA}>{um ? `Pela regra${um.centro_regra ? ` (${operacoes.rotulo(um.centro_regra)})` : ''}` : 'Voltar pra regra automática'}</option>
                {operacoes.lista.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
              </select>
              {!daFatura.length && <div style={{ ...nota, marginTop: 4 }}>Só a linha da fatura tem operação.</div>}
            </div>
          </div>
        </>
      ) : (
        <div>
          <div style={{ ...nota, marginBottom: 10 }}>
            Fecha a linha da fatura como gasto pessoal, sem lançamento no TechMiles. A categoria fica.
            Pessoal X/J é a retirada do sócio — entra no acerto dos sócios.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            {[['PESSOAL', 'Pessoal'], ['PESSOAL_X', 'Pessoal X'], ['PESSOAL_J', 'Pessoal J']].map(([v, r]) => (
              <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: colors.text, cursor: 'pointer' }}>
                <input type="radio" name="pessoal" checked={pessoal === v} onChange={() => setPessoal(v)} /> {r}
              </label>
            ))}
          </div>
          <div style={nota}>
            {pessoaisPossiveis.length} de {grupos.length} podem ser marcada(s)
            {pessoaisPossiveis.length < grupos.length ? ' — as outras já estão conciliadas ou não têm linha de fatura.' : '.'}
          </div>
        </div>
      )}

      {resultado && (
        <div style={{ marginTop: 14, padding: 10, borderRadius: 8, fontSize: 13,
          border: `1px solid ${resultado.erros.length ? colors.warning : colors.success}`, color: colors.text }}>
          {resultado.feitos.map((f, i) => <div key={`f${i}`} style={{ color: colors.success }}>✓ {f}</div>)}
          {resultado.erros.map((e, i) => <div key={`e${i}`} style={{ color: colors.warning }}>⚠ {e}</div>)}
          {!resultado.feitos.length && !resultado.erros.length && <div style={{ color: colors.textMuted }}>Nada a mudar.</div>}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button variant="outline" color={colors.textMuted} onClick={onClose}>{resultado ? 'Fechar' : 'Cancelar'}</Button>
        {!resultado && (
          <Button onClick={aplicar} disabled={busy || (modo === 'pessoal' ? !pessoaisPossiveis.length
            : !((mudarCategoria && temCategoria) || empresa !== empresaInicial || operacao !== operacaoInicial))}>
            {busy ? 'Aplicando…' : modo === 'pessoal' ? `Marcar ${pessoaisPossiveis.length} como pessoal` : 'Aplicar'}
          </Button>
        )}
      </div>
    </Modal>
  );
}
